import React from 'react';
import { Database } from 'lucide-react';
import LoadingDots from './LoadingDots';

const ChatMessage = ({ message, index }) => {
  if (message.isLoading) {
    return (
      <div
        key={index}
        className="p-4 rounded-lg mb-4 bg-white mr-12"
      >
        <div className="flex items-start">
          <div className="bg-primary p-2 rounded-full mr-3">
            <Database className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <LoadingDots />
            {message.isFileUpload && (
              <div className="text-sm text-gray-500 mt-1">Uploading file...</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const formatText = (text) => {
    if (!text) return '';

    // Process numbered lists with headings and descriptions
    const formattedText = text.split('\n').map((line, i) => {
      // Check for numbered list items with bold headings
      const numberedItemMatch = line.match(/^(\d+)\.\s+\*\*(.+?)\*\*(.*)$/);
      if (numberedItemMatch) {
        return (
          <div key={i} className="mb-3">
            <div className="flex">
              <span className="font-bold mr-2">{numberedItemMatch[1]}.</span>
              <div>
                <span className="font-bold">{numberedItemMatch[2]}</span>
                {numberedItemMatch[3]}
              </div>
            </div>
          </div>
        );
      }

      // Check for description items with dashes
      const descriptionMatch = line.match(/^\s*-\s+\*\*(.+?)\*\*\s*:(.*)$/);
      if (descriptionMatch) {
        return (
          <div key={i} className="ml-6 mb-2">
            <span className="font-bold">{descriptionMatch[1]}:</span>
            {descriptionMatch[2]}
          </div>
        );
      }

      // Bold text between asterisks
      let processedLine = line;
      const boldMatches = [...line.matchAll(/\*\*(.+?)\*\*/g)];
      if (boldMatches.length > 0) {
        processedLine = (
          <span key={i}>
            {line.split(/\*\*(.+?)\*\*/).map((part, j) => {
              return j % 2 === 1 ? <strong key={j}>{part}</strong> : part;
            })}
          </span>
        );
        return <div key={i} className="mb-1">{processedLine}</div>;
      }

      // Regular line
      return <div key={i} className="mb-1">{line}</div>;
    });

    return <div>{formattedText}</div>;
  };

  return (
    <div
      className={`p-4 rounded-lg mb-4 ${message.role === 'user' ? 'bg-blue-50 ml-12' : 'bg-white mr-12'}`}
    >
      <div className="flex items-start">
        {message.role === 'assistant' && (
          <div className="bg-primary p-2 rounded-full mr-3">
            <Database className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex-1">
          <div className="text-gray-800">
            {formatText(message.content)}
          </div>

          {message.sql_query && (
            <div className="mt-3 p-3 bg-gray-800 text-white rounded-md overflow-x-auto">
              <pre className="text-sm">{message.sql_query}</pre>
            </div>
          )}

          {message.sqlResults && Array.isArray(message.sqlResults) && message.sqlResults.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr>
                    {Object.keys(message.sqlResults[0] || {}).map((key, i) => (
                      <th key={i} className="py-2 px-4 border-b border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {message.sqlResults.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {Object.values(row).map((value, j) => (
                        <td key={j} className="py-2 px-4 border-b border-gray-200 text-sm">
                          {value !== null ? value.toString() : 'NULL'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;